import { useEffect, useRef } from 'react';

const stats = [['strength', 'Strength'], ['agility', 'Agility'], ['constitution', 'Constitution'],
  ['intellect', 'Intellect'], ['wisdom', 'Wisdom'], ['charisma', 'Charisma']];
const skillNames = { 1: 'Health', 2: 'Combat', 3: 'Physical', 4: 'Stealth', 5: 'Perception',
  6: 'Magic', 7: 'Meditation', 8: 'Academic', 9: 'Social' };

export const removeDraftAllocationPoint = (draft, point) => {
  const index = draft.map(entry => entry.kind === point.kind && entry.id === point.id).lastIndexOf(true);
  return index < 0 ? draft : [...draft.slice(0, index), ...draft.slice(index + 1)];
};

function AllocationConfirmation({ children, busy, onSave, onCancel, error }) {
  const dialog = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    dialog.current?.focus();
    return () => previous?.focus();
  }, []);
  return <div className="play-advancement-confirmation" role="dialog" aria-modal="true"
    aria-label="Confirm level allocation" tabIndex={-1} ref={dialog} onKeyDown={event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); if (!busy) onCancel(); }
      if (event.key !== 'Tab') return;
      const controls = [...dialog.current.querySelectorAll('button:not(:disabled)')];
      const first = controls[0], last = controls[controls.length - 1];
      if (!controls.length) event.preventDefault();
      else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) { event.preventDefault(); first.focus(); }
    }}>
    <div className="play-advancement-dialog">
      <h2>Confirm level allocation</h2>
      {children}
      <p>Save these increases and begin the next level?</p>
      {error && <p role="alert">{error}</p>}
      <div className="button-row">
        <button type="button" disabled={busy} onClick={onSave}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="secondary-button" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
      <p>Cancel undoes the last point added.</p>
    </div>
  </div>;
}

export default function LevelAdvancement({ character, progression, primarySkillOptions = [], draft = [], onAdd,
  onRemove, onCancel, onSave, busy = false, error = '' }) {
  const pending = progression.phase === 'allocationRequired';
  const statDraft = draft.filter(point => point.kind === 'stat');
  const skillDraft = draft.filter(point => point.kind === 'skill');
  const statRemaining = (progression.statPoints || 0) - statDraft.length;
  const skillRemaining = progression.kitty - skillDraft.length;
  const skillPoints = id => skillDraft.filter(point => point.id === Number(id)).length;
  const name = id => primarySkillOptions.find(row => Number(row.idPrimarySkillList) === Number(id))?.name || skillNames[id];
  const complete = pending && statRemaining === 0 && skillRemaining === 0;
  return <>
    {pending && <p className="character-advancement-notice">Level {progression.transition.nextLevel} earned. Allocate your points to begin the next level. You can reassign manually added points until you Save.</p>}
    <section className="character-sheet-section">
      <div className="character-sheet-section-header"><h2>Stats</h2>
        {pending && <span aria-live="polite">{statRemaining} stat points remaining</span>}</div>
      {pending && <p>Add one point to each of two different stats.</p>}
      <div className="character-point-grid">
        {stats.map(([key, label]) => {
          const added = statDraft.some(point => point.id === key);
          return <div key={key} className={`character-point-row${pending ? '' : ' character-point-row-readonly'}`}>
            <span>{label}</span>
            {pending && <button type="button" aria-label={`Remove one point from ${label}`} disabled={busy || !added}
              onClick={() => onRemove({ kind: 'stat', id: key })}>−</button>}
            <strong>{Number(character[key]) || 0}{added && <em className="advancement-draft"> +1</em>}</strong>
            {pending && <button type="button" aria-label={`Add one point to ${label}`} disabled={busy || added || statRemaining <= 0}
              onClick={() => onAdd({ kind: 'stat', id: key })}>+</button>}
          </div>;
        })}
      </div>
    </section>
    <section className="character-sheet-section">
      <div className="character-sheet-section-header"><h2>Primary Abilities</h2>
        {pending && <span aria-live="polite">{skillRemaining} skill points remaining</span>}</div>
      <p>Each skill can gain at most two points per level, including points learned automatically.</p>
      <div className="character-point-grid">
        {Object.entries(progression.primary).map(([id, skill]) => {
          const added = skillPoints(id), room = pending ? progression.transition.room[id] : skill.room;
          return <div key={id} className={`character-point-row${pending ? '' : ' character-point-row-readonly'}`}>
            <span className="character-point-label"><span>{name(id)}</span>
              <em>{skill.gains} earned{added > 0 ? ` + ${added} allocated` : ''}; {skill.gains + added}/2 this level</em>
              {pending && room <= added && <em>Level allowance used</em>}</span>
            {pending && <button type="button" aria-label={`Remove one point from ${name(id)}`} disabled={busy || added === 0}
              onClick={() => onRemove({ kind: 'skill', id: Number(id) })}>−</button>}
            <strong>{skill.rank}{added > 0 && <em className="advancement-draft"> +{added}</em>}</strong>
            {pending && <button type="button" aria-label={`Add one point to ${name(id)}`}
              disabled={busy || skillRemaining <= 0 || room <= added} onClick={() => onAdd({ kind: 'skill', id: Number(id) })}>+</button>}
          </div>;
        })}
      </div>
    </section>
    {complete && <AllocationConfirmation busy={busy} onSave={onSave} onCancel={onCancel} error={error}>
      <ul className="play-advancement-changes">{statDraft.map(point => {
        const label = stats.find(([key]) => key === point.id)[1];
        return <li key={point.id}>{label} +1</li>;
      })}
        {[...new Set(skillDraft.map(point => point.id))].map(id => <li key={id}>{name(id)} +{skillPoints(id)}</li>)}</ul>
    </AllocationConfirmation>}
  </>;
}
